import { useDebounce } from "@aurem/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { companyDashboard, editEmployee } from "../../../const/routes";
import { useNavigation } from "../../../hooks/navigate";
import { useGlobalSearch } from "../hooks";
import {
  GlobalSearchContextType,
  GlobalSearchProviderProps,
  isSearchCompany,
  isSearchEmployee,
  SearchCompany,
  SearchEmployee,
  SearchItem,
} from "../types";

const RECENT_SEARCHES_KEY = "recent_searches";
const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_LIMIT = 10;

type UseViewModelReturn = { context: GlobalSearchContextType };

export const useViewModel = ({
  open,
  onDismiss,
}: GlobalSearchProviderProps): UseViewModelReturn => {
  const navigate = useNavigation();

  const [query, setQuery] = useState<string>("");
  const [globalSearchQuery, { data, loading }] = useGlobalSearch();

  const { debouncedValue } = useDebounce<string>(query, SEARCH_DEBOUNCE_MS);
  const prevDebouncedValueRef = useRef(debouncedValue);

  const [recentSearches, setRecentSearches] = useState<SearchItem[]>(() => {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? (JSON.parse(stored) as SearchItem[]) : [];
  });

  useEffect(() => {
    if (debouncedValue && debouncedValue !== prevDebouncedValueRef.current) {
      void globalSearchQuery({
        variables: {
          query: debouncedValue,
          pagination: { offset: 0, limit: SEARCH_LIMIT },
        },
      });
      prevDebouncedValueRef.current = debouncedValue;
    }
  }, [debouncedValue, globalSearchQuery]);

  const handleItemClick = useCallback(
    (selected: SearchItem): void => {
      const isDuplicate = recentSearches.some(
        (item) => JSON.stringify(item) === JSON.stringify(selected),
      );

      if (!isDuplicate) {
        const updatedSearches = [...recentSearches, selected];
        localStorage.setItem(
          RECENT_SEARCHES_KEY,
          JSON.stringify(updatedSearches),
        );
        setRecentSearches(updatedSearches);
      }

      if (isSearchCompany(selected)) {
        void navigate(companyDashboard({ companyId: selected.id }));
      } else if (isSearchEmployee(selected)) {
        void navigate(
          editEmployee({
            employeeId: selected.id,
            companyId: selected.company.id,
          }),
        );
      }

      onDismiss();
    },
    [recentSearches, navigate, onDismiss],
  );

  const clearRecentSearches = useCallback((): void => {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
    setRecentSearches([]);
  }, []);

  const companies = useMemo((): SearchCompany[] => {
    return data?.searchCompanies.list ?? [];
  }, [data]);

  const employees = useMemo((): SearchEmployee[] => {
    return data?.searchEmployees.list ?? [];
  }, [data]);

  const context = useMemo((): GlobalSearchContextType => {
    return {
      query,
      setQuery,
      companies,
      employees,
      loading,
      recentSearches,
      handleItemClick,
      clearRecentSearches,
      onDismiss,
      open,
    };
  }, [
    query,
    companies,
    employees,
    loading,
    recentSearches,
    handleItemClick,
    clearRecentSearches,
    onDismiss,
    open,
  ]);

  return { context };
};
